import React, { useMemo, useState, useEffect } from "react";
import { Button, Select, Tabs } from "antd";
import { Plus } from "lucide-react";
import { DataTable } from "../../components/DataTable";
import { useModalStore } from "../../../store/slices/modal";
import {
  useMasterList,
  useMasterCreate,
  useMasterUpdate,
  useMasterDelete,
} from "../../../hooks/api/master/hooks";
import { IMaster, MasterType } from "../../../types/api/master";
import { MODAL_TYPES } from "../../../types/modal-types";
import { getMasterColumns } from "./MasterColumns";
import { MasterFormModal, MasterFormValues } from "./MasterFormModal";

import { useMessageApi } from "../../../provider/AntdNotification";

const MASTER_TYPE_OPTIONS: { label: string; value: MasterType }[] = [
  { label: "Subject", value: "SUBJECT" },
  { label: "Style", value: "STYLE" },
  { label: "Medium", value: "MEDIUM" },
  { label: "Material", value: "MATERIAL" },
  { label: "Finish", value: "FINISH" },
  { label: "Method", value: "METHOD" },
  { label: "Base", value: "BASE" },
  { label: "Tags", value: "TAGS" },
];

const MASTER_TABS = [
  { key: "SUBJECT", label: "Subject" },
  { key: "STYLE", label: "Style" },
  { key: "MEDIUM", label: "Medium" },
  { key: "MATERIAL", label: "Material" },
  { key: "FINISH", label: "Finish" },
  { key: "METHOD", label: "Method" },
  { key: "BASE", label: "Base" },
  { key: "TAGS", label: "Tags" },
];

const MasterList: React.FC = () => {
  const messageApi = useMessageApi();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState<MasterType>("SUBJECT");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const { type, data, open, close } = useModalStore();

  const isModalOpen =
    type === MODAL_TYPES.CREATE_MASTER || type === MODAL_TYPES.EDIT_MASTER;
  const editingMaster =
    type === MODAL_TYPES.EDIT_MASTER ? (data as IMaster) : null;

  const queryData = useMemo(() => {
    const q: any = { page, limit };
    if (search.trim()) q.search = search.trim();
    if (statusFilter !== "") q.status = statusFilter;
    if (typeFilter) q.type = typeFilter;
    return q;
  }, [page, limit, search, statusFilter, typeFilter]);

  const { data: listRes, isLoading, isError, error, refetchWithQuery } =
    useMasterList(queryData);
  const masters: IMaster[] = (listRes?.data as IMaster[]) ?? [];
  const totalMasters = Number(listRes?.extra?.total ?? masters.length);

  useEffect(() => {
    refetchWithQuery(queryData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryData]);

  const { mutate: createMaster, isPending: creating } = useMasterCreate({
    qData: queryData,
    onSuccess: () => {
      messageApi.success("Master created.");
      close();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message ?? err?.message ?? "Failed to create master"),
  });

  const { mutate: updateMaster, isPending: updating } = useMasterUpdate({
    qData: queryData,
    onSuccess: () => {
      messageApi.success("Master updated.");
      close();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message ?? err?.message ?? "Failed to update master"),
  });

  const { mutate: deleteMaster } = useMasterDelete({
    qData: queryData,
    onSuccess: () => messageApi.success("Master deleted."),
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message ?? err?.message ?? "Failed to delete master"),
  });

  const handleFormSubmit = (values: MasterFormValues) => {
    if (editingMaster?._id) {
      updateMaster({ _id: editingMaster._id, ...values } as any);
    } else {
      createMaster(values as any);
    }
  };

  const columns = getMasterColumns({
    onEdit: (record) => open(MODAL_TYPES.EDIT_MASTER, record),
    onDelete: (id) => deleteMaster(id),
  });

  return (
    <div className="p-6 min-h-screen">
      <div className="bg-white rounded-lg p-4 shadow-sm mb-4">
        <Tabs
          activeKey={typeFilter}
          onChange={(key) => {
            setTypeFilter(key as MasterType);
            setPage(1);
          }}
          items={MASTER_TABS}
          className="mb-0"
        />
      </div>

      <DataTable
        title="Masters"
        headerActions={
          <Button
            type="primary"
            size="small"
            icon={<Plus size={14} />}
            onClick={() => open(MODAL_TYPES.CREATE_MASTER)}
            className="!bg-primary flex items-center gap-1"
          >
            Add Master
          </Button>
        }
        dataSource={masters}
        columns={columns}
        loading={isLoading}
        isError={isError}
        errorMessage={(error as any)?.message}
        rowKey="_id"
        searchProps={{
          searchValue: search,
          onSearchChange: (v) => { setSearch(v); setPage(1); },
          placeholder: "Search masters...",
          statusValue: statusFilter,
          onStatusChange: (v) => { setStatusFilter(v); setPage(1); },
          children: (
            <Select
              value={typeFilter}
              onChange={(v) => { setTypeFilter(v as MasterType); setPage(1); }}
              options={MASTER_TYPE_OPTIONS}
              className="w-36 h-[35px]"
            />
          ),
        }}
        paginationProps={{
          page,
          limit,
          total: totalMasters,
          onChange: (newPage, newLimit) => {
            if (newLimit !== limit) {
              setLimit(newLimit);
              setPage(1);
              refetchWithQuery({ ...queryData, page: 1, limit: newLimit });
            } else {
              setPage(newPage);
              refetchWithQuery({ ...queryData, page: newPage });
            }
          },
        }}
      />

      <MasterFormModal
        open={isModalOpen}
        onClose={close}
        onSubmit={handleFormSubmit}
        submitting={creating || updating}
        editingMaster={editingMaster}
        defaultType={typeFilter}
      />
    </div>
  );
};

export default MasterList;
